import { forwardRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Phone input with globally-enforced rules:
 *   - digits only
 *   - leading zeros stripped automatically
 *   - hard cap at 10 digits (both onChange and via maxLength)
 *   - shows a validation message once the user has touched the field.
 *
 * Value is always a normalised 10-digit string (or shorter while typing).
 * Emits `onValidChange(bool)` so parent forms can gate submission.
 */
export const PhoneInput = forwardRef(function PhoneInput(
    { value, onChange, onValidChange, testid, placeholder = "10-digit mobile number", required = true, disabled = false },
    ref,
) {
    const [touched, setTouched] = useState(false);
    const isValid = /^\d{10}$/.test(value || "");

    useEffect(() => {
        if (typeof onValidChange === "function") onValidChange(isValid);
    }, [isValid, onValidChange]);

    const handle = (raw) => {
        // Digits only, strip leading zeros, cap at 10.
        let cleaned = String(raw || "").replace(/\D/g, "").replace(/^0+/, "");
        if (cleaned.length > 10) cleaned = cleaned.slice(0, 10);
        onChange(cleaned);
    };

    return (
        <div>
            <Input
                ref={ref}
                data-testid={testid}
                value={value || ""}
                onChange={(e) => handle(e.target.value)}
                onBlur={() => setTouched(true)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder={placeholder}
                aria-invalid={touched && !isValid}
                required={required}
                disabled={disabled}
            />
            {touched && !isValid && (
                <p
                    data-testid={`${testid}-error`}
                    className="text-[11px] text-red-600 mt-1"
                >
                    Enter a valid 10-digit mobile number.
                </p>
            )}
        </div>
    );
});

/**
 * OTP input with rules: numeric only, exactly 6 digits, no alphabets / special chars.
 */
export const OtpInput = forwardRef(function OtpInput(
    { value, onChange, onValidChange, testid, disabled = false },
    ref,
) {
    const [touched, setTouched] = useState(false);
    const isValid = /^\d{6}$/.test(value || "");

    useEffect(() => {
        if (typeof onValidChange === "function") onValidChange(isValid);
    }, [isValid, onValidChange]);

    const handle = (raw) => {
        let cleaned = String(raw || "").replace(/\D/g, "");
        if (cleaned.length > 6) cleaned = cleaned.slice(0, 6);
        onChange(cleaned);
    };

    return (
        <div>
            <Input
                ref={ref}
                data-testid={testid}
                value={value || ""}
                onChange={(e) => handle(e.target.value)}
                onBlur={() => setTouched(true)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="6-digit code"
                aria-invalid={touched && !isValid}
                required
                disabled={disabled}
            />
            {touched && !isValid && (
                <p
                    data-testid={`${testid}-error`}
                    className="text-[11px] text-red-600 mt-1"
                >
                    OTP must be exactly 6 digits.
                </p>
            )}
        </div>
    );
});
